/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { PersonInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Properties for the PrintPatientRegistrationCard template component.
 * 
 * @interface PrintPatientRegistrationCardProps
 * @property {Partial<PersonInterface>} personModel - Person/patient data to display
 * @property {number} age - Patient age in years
 * @property {Function} t - Translation function for i18n
 * @property {string} [photoBase64] - Base64 encoded photo for embedding
 */
interface PrintPatientRegistrationCardProps {
  personModel: Partial<PersonInterface>;
  age: number;
  t: (key: string) => string;
  photoBase64?: string;
}

/**
 * Generates a printable patient registration card.
 * Creates a formatted HTML document with patient demographics and contact information.
 * 
 * Features:
 * - Professional medical registration card format
 * - Patient photo (if available)
 * - Account number prominently displayed
 * - Full name with prefix/suffix
 * - Date of birth and calculated age
 * - Sex/gender
 * - Contact information (phones)
 * - Emergency contacts
 * - Complete address
 * - Print-friendly styling
 * 
 * Layout Structure:
 * - Header with clinic/facility name
 * - Patient photo section
 * - Account number (highlighted)
 * - Personal information section
 * - Contact information section
 * - Emergency contacts section
 * - Address section
 * 
 * @param {PrintPatientRegistrationCardProps} props - Template properties
 * @returns {string} Complete HTML document ready for printing
 */
export const PrintPatientRegistrationCard = (props: PrintPatientRegistrationCardProps): string => {
  const { personModel, age, t, photoBase64 } = props;

  const formattedDob = personModel?.dob?.split("T")[0] || "";
  const sex = personModel?.sex === "MALE" ? t("headerPersonInformation.male") : t("headerPersonInformation.female");
  
  // Format phones for display
  const phonesHtml = personModel?.phones?.length
    ? personModel.phones
        .map((phone) => `<p><strong>${t("printRegistrationCard.phone")} ${phone.idx}:</strong> ${phone.number}</p>`)
        .join("")
    : `<p>${t("printRegistrationCard.noPhones")}</p>`;

  // Build full name with prefix/suffix
  const fullName = [
    personModel?.prefix || "",
    personModel?.name || "",
    personModel?.mi || "",
    personModel?.lastname || "",
    personModel?.suffix || "",
  ]
    .filter(Boolean)
    .join(" ");

  const style = `
    <style>
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
      
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 1px;
        background: #fff;
      }
      
      .registration-card {
        max-width: 600px;
        margin: 0 auto;
        border: 2px solid #333;
        border-radius: 8px;
        overflow: hidden;
      }
      
      .card-header {
        background: #1976d2;
        color: white;
        padding: 15px;
        text-align: center;
      }
      
      .card-header h1 {
        margin: 0;
        font-size: 1.5em;
      }
      
      .card-header h2 {
        margin: 5px 0 0 0;
        font-size: 1em;
        font-weight: normal;
      }
      
      .card-body {
        padding: 20px;
      }
          
      .account-number {
        background: #f5f5f5;
        border: 2px solid #1976d2;
        border-radius: 8px;
        padding: 15px;
        text-align: center;
        margin-bottom: 20px;
      }
      
      .account-number label {
        font-size: 0.9em;
        color: #666;
        display: block;
        margin-bottom: 5px;
      }
      
      .account-number .number {
        font-size: 1.8em;
        font-weight: bold;
        color: #1976d2;
      }
      
      .patient-name {
        text-align: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid #ddd;
      }
      
      .patient-name h3 {
        margin: 0;
        font-size: 1.4em;
        color: #333;
      }
      
      .info-section {
        margin-bottom: 20px;
      }
      
      .info-section h4 {
        background: #f5f5f5;
        padding: 8px 12px;
        margin: 0 0 10px 0;
        border-left: 4px solid #1976d2;
        font-size: 1em;
      }
      
      .info-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        padding: 0 12px;
        align-items: center;
      }
      
      .info-item {
        display: flex;
        align-items: center;
        gap: 5px;
      }
      
      .info-item label {
        font-size: 0.85em;
        color: #666;
        margin: 0;
      }
      
      .info-item .value {
        font-weight: bold;
        color: #333;
      }
      
      .contact-section p,
      .address-section p {
        margin: 5px 0;
        padding: 0 12px;
      }
      
      .card-footer {
        background: #f5f5f5;
        padding: 10px 20px;
        text-align: center;
        font-size: 0.8em;
        color: #666;
        border-top: 1px solid #ddd;
      }
    </style>
  `;

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${t("printRegistrationCard.title")} - ${fullName}</title>
      ${style}
    </head>
    <body>
      <div class="registration-card">
        <div class="card-header">
          <h1>${t("printRegistrationCard.title")}</h1>
          <h2>${t("printRegistrationCard.subtitle")}</h2>
        </div>
        
        <div class="card-body">
         
          ${personModel?.patient?.accountNumber ? `
          <div class="account-number">
            <label>${t("printRegistrationCard.accountNumber")}</label>
            <div class="number">${personModel.patient.accountNumber}</div>
          </div>
          ` : ""}
          
          <div class="patient-name">
            <h3>${fullName}</h3>
          </div>
          
          <div class="info-section">
            <h4>${t("printRegistrationCard.personalInformation")}</h4>
            <div class="info-grid">
              <div class="info-item">
                <label>${t("printRegistrationCard.dateOfBirth")}:</label>
                <span class="value">${formattedDob}</span>
              </div>
              <div class="info-item">
                <label>${t("printRegistrationCard.age")}:</label>
                <span class="value">${age} ${t("printRegistrationCard.years")}</span>
              </div>
              <div class="info-item">
                <label>${t("printRegistrationCard.sex")}:</label>
                <span class="value">${sex}</span>
              </div>
              ${personModel?.identity ? `
              <div class="info-item">
                <label>${t("printRegistrationCard.identity")}:</label>
                <span class="value">${personModel.identity}</span>
              </div>
              ` : ""}
              ${personModel?.ethnicity ? `
              <div class="info-item">
                <label>${t("printRegistrationCard.ethnicity")}:</label>
                <span class="value">${personModel.ethnicity}</span>
              </div>
              ` : ""}
            </div>
          </div>
          
          <div class="info-section contact-section">
            <h4>${t("printRegistrationCard.contactInformation")}</h4>
            ${phonesHtml}
          </div>
          
          <div class="info-section address-section">
            <h4>${t("printRegistrationCard.address")}</h4>
            <p>
              ${[
                personModel?.address,
                personModel?.city,
                personModel?.state,
                personModel?.country,
                personModel?.zip
              ].filter(Boolean).join(", ") || t("printRegistrationCard.noAddress")}
            </p>
          </div>
        </div>
        
        <div class="card-footer">
          ${t("printRegistrationCard.printedOn")}: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default PrintPatientRegistrationCard;
